import Axios from '../../utils/axios';
import { getBackendErrorMessage } from '../../utils/errorHandler';
import type { ItemCondition, ItemStatus, ItemStatusV2 } from './status';

export interface InboundItemShipment {
  providerOrderId: string;
  /** The shipment's Booking ID (AWB). Distinct from providerOrderId (waybill). */
  bookingId: string;
  /** The originating ts-delivery delivery UID (= external_delivery_uid). This is
   *  the value scanned in the scan-by-delivery-uid flow. Null for CSV imports. */
  externalDeliveryUid: string | null;
  receiverName: string;
}

export interface InboundItemBatch {
  id: string;
  fileName: string;
}

/** Full read vocabulary (v2 + pre-migration values) — see `./status`. */
export type InboundItemStatus = ItemStatus;

/**
 * Statuses the inbound list can filter on (09 §3). Narrower than the full item
 * vocabulary: only what can plausibly sit at — or be headed back to — a hub.
 * Anything else 400s.
 *
 * Two absences, both deliberate: `DAMAGED` (damage is `condition` now, so use
 * `condition=DAMAGED`) and `DISPATCHED` — that status is alive and well, it just
 * belongs to the dispatch screen, not the hub floor. `/v1/packages` accepts it.
 */
export const INBOUND_STATUS_FILTERS = [
  'CREATED',
  'HUB_SCANNED',
  'HANDOVER_SCANNED',
  'IN_DELIVERY',
  'IN_RETURN',
  'RETURNED_TO_HUB',
  'LOST',
] as const satisfies readonly ItemStatusV2[];
export type InboundStatusFilter = (typeof INBOUND_STATUS_FILTERS)[number];

export interface InboundItem {
  id: string;
  code: string;
  itemName: string;
  /** Customer invoice number (items.invoice). `""` when the upload had none.
   *  Searchable alongside item name / waybill; shown in the UI when it differs
   *  from itemName. */
  invoice: string;
  /** Physical parcel count (dus) for this item — how many boxes to expect at the hub. */
  koli: number;
  status: InboundItemStatus;
  /**
   * Physical condition — replaced item status `DAMAGED` in v2, so a damaged
   * parcel keeps its real custody status and the flag survives the journey.
   */
  condition?: ItemCondition | null;
  hubId: string | null;
  scannedAt: string | null;
  destinationH3Index: string;
  zoneCode: string | null;
  zoneName: string | null;
  zoneColor: string | null;
  clientName: string | null;
  shipment: InboundItemShipment;
  uploadShipment: InboundItemBatch;
  handedOverAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InboundItemsResponse {
  items: InboundItem[];
  total: number;
  page: number;
  limit: number;
}

export interface InboundBatch {
  id: string;
  fileName: string;
  totalItems: number;
  uploadedAt: string;
  status: string;
}

export interface InboundBatchesResponse {
  batches: InboundBatch[];
}

export interface ScanItemRequest {
  scan_uid: string;
  item_name: string;
  upload_shipment_id?: string;
  hub_id: number;
}

export interface ScannedItem {
  id: string;
  code: string;
  itemName: string;
  /** The originating ts-delivery delivery UID (= external_delivery_uid). Null
   *  for CSV-import items (no linked delivery). */
  externalDeliveryUid: string | null;
  status: string;
  hubId: string | null;
  scannedAt: string | null;
  zoneCode: string | null;
  zoneName: string | null;
  zoneColor: string | null;
  destinationH3Index: string;
}

export interface ScanItemSuccessResponse {
  item: ScannedItem;
  duplicate?: boolean;
}

export const getInboundItems = async (params: {
  hub_id?: number;
  client_id?: number;
  upload_shipment_id?: string;
  search?: string;
  /** See `INBOUND_STATUS_FILTERS` — `DAMAGED` / `DISPATCHED` both 400 here. */
  status?: 'ALL' | InboundStatusFilter;
  /** Damaged-parcel filter, orthogonal to `status`. */
  condition?: ItemCondition;
  created_from?: string;
  created_to?: string;
  page?: number;
  limit?: number;
}): Promise<InboundItemsResponse> => {
  try {
    const response = await Axios.get('/v1/inbound/items', { params });
    return response.data.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const getInboundBatches = async (params?: {
  status?: string;
}): Promise<InboundBatchesResponse> => {
  try {
    const response = await Axios.get('/v1/inbound/upload-shipments', {
      params,
    });
    return response.data.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const scanItem = async (
  req: ScanItemRequest
): Promise<ScanItemSuccessResponse> => {
  try {
    const response = await Axios.post('/v1/inbound/items/scan', req);
    return response.data.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * `GET /v1/inbound/items/counts` — one bucket per status tab (09 §2).
 *
 * ⚠️ **Name trap.** `verifiedByHub` / `verifiedByRider` count status
 * `HUB_SCANNED` / `HANDOVER_SCANNED`; there is no `VERIFIED_BY_*` status. The
 * key names survived a withdrawn rename.
 *
 * Two buckets are gone and both matter: **`damaged`** (damage is the `condition`
 * attribute now — count it with `condition=DAMAGED`) and **`dispatched`**, which
 * is absent *on purpose* here while `/v1/packages/summary` does have it. This
 * screen is the hub floor; committed-dispatch parcels are watched from the
 * dispatch screen. So **`all` is the hub-floor total, not the AWB item total** —
 * never use it for "how many POs are there".
 *
 * Everything but `all` is optional so a BE that omits an empty bucket — or an
 * older build — degrades to a blank chip instead of throwing. Read through
 * `readItemCount` so both key spellings work.
 */
export interface ItemCounts {
  all: number;
  created?: number;
  /** Status `HUB_SCANNED` — see the name trap above. */
  verifiedByHub?: number;
  /** Status `HANDOVER_SCANNED` — see the name trap above. */
  verifiedByRider?: number;
  inDelivery?: number;
  inReturn?: number;
  returnedToHub?: number;
  lost?: number;
  /** Fallback spelling for `verifiedByHub`. */
  hubScanned?: number;
  /** Fallback spelling for `verifiedByRider`. */
  handoverScanned?: number;
}

/** Count bucket per status filter, tolerant of both key spellings. */
export const readItemCount = (
  counts: ItemCounts,
  status: InboundStatusFilter
): number | null => {
  switch (status) {
    case 'CREATED':
      return counts.created ?? null;
    case 'HUB_SCANNED':
      return counts.verifiedByHub ?? counts.hubScanned ?? null;
    case 'HANDOVER_SCANNED':
      return counts.verifiedByRider ?? counts.handoverScanned ?? null;
    case 'IN_DELIVERY':
      return counts.inDelivery ?? null;
    case 'IN_RETURN':
      return counts.inReturn ?? null;
    case 'RETURNED_TO_HUB':
      return counts.returnedToHub ?? null;
    case 'LOST':
      return counts.lost ?? null;
    default:
      return null;
  }
};

export interface MarkExceptionResult {
  id: string;
  itemName: string;
  status: string;
  /** v2: `DAMAGED` writes this instead of touching `status`. */
  condition?: ItemCondition | null;
  /** Parent AWB — returned so the caller can refresh its rollup. */
  shipmentId?: string;
  hubId: string | null;
  scannedAt: string | null;
}

/**
 * Flag an item at the hub.
 *   - `LOST` → item status `LOST` (reversible; may roll the AWB up to `FAILED`)
 *   - `DAMAGED` → sets `condition = DAMAGED`, **status untouched** (v2)
 *   - `REVERT` → clears `condition`; un-loses a `LOST` item back to
 *     `CREATED`/`HUB_SCANNED`
 */
export const markException = async (
  itemId: string,
  body: { action: 'LOST' | 'DAMAGED' | 'REVERT'; note?: string }
): Promise<MarkExceptionResult> => {
  try {
    const response = await Axios.post(
      `/v1/inbound/items/${itemId}/exception`,
      body
    );
    return response.data.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const getItemCounts = async (params: {
  hub_id?: number;
  client_id?: number;
  search?: string;
  created_from?: string;
  created_to?: string;
}): Promise<ItemCounts> => {
  try {
    const response = await Axios.get('/v1/inbound/items/counts', { params });
    return response.data.data;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const inboundAPI = {
  getInboundItems,
  getInboundBatches,
  getItemCounts,
  markException,
  scanItem,
};
